/**
 * Message Service
 *
 * Manages chat messages including sending, editing, and deletion.
 * Uses cursor-based pagination for efficient message loading.
 */
import type { SupabaseClient } from '@supabase/supabase-js';

import { Result, ServiceError } from '@kit/shared/result';
import { BaseService } from '@kit/shared/services';
import {
  batchGetProfilePhotoSignedUrls,
  getProfilePhotoSignedUrl,
} from '@kit/shared/storage';
import type { Database } from '@kit/supabase/database';

import { MESSAGE_ORG_SENDER_EMBEDS } from '../../lib/org-conversation';
import type {
  GetMessagesParams,
  GetMessagesResult,
  Message,
  MessageWithSender,
  SendMessageParams,
} from '../../types';

const DEFAULT_PAGE_SIZE = 50;

export class MessageService extends BaseService {
  constructor(client: SupabaseClient<Database>) {
    super(client);
  }

  /**
   * Get messages for a conversation with cursor-based pagination
   * Returns messages in reverse chronological order (newest first for display)
   */
  async getMessages(
    params: GetMessagesParams,
  ): Promise<Result<GetMessagesResult>> {
    const { conversationId, cursor, limit = DEFAULT_PAGE_SIZE } = params;

    const uuidValidation = this.validateUUID(conversationId, 'conversationId');
    if (uuidValidation.isFailure) {
      return Result.fail(uuidValidation.getError());
    }

    let query = this.client
      .from('messages')
      .select(
        `
        *,
        sender:profiles!messages_sender_id_profiles_fkey(id, first_name, last_name, profile_photo_url),
        ${MESSAGE_ORG_SENDER_EMBEDS}
      `,
      )
      .eq('conversation_id', conversationId)
      .eq('is_deleted', false)
      .order('created_at', { ascending: false })
      .limit(limit + 1); // Fetch one extra to determine if there are more

    // Apply cursor if provided (cursor is the created_at of the last message)
    if (cursor) {
      query = query.lt('created_at', cursor);
    }

    const { data, error } = await query;

    if (error) {
      return Result.fail(this.handleDatabaseError(error));
    }

    const messages = data || [];
    const hasMore = messages.length > limit;

    // Remove the extra message we fetched
    if (hasMore) {
      messages.pop();
    }

    // Determine next cursor
    const nextCursor = hasMore
      ? messages[messages.length - 1]?.created_at || null
      : null;

    // Generate signed URLs for sender profile photos (storage paths → signed URLs)
    const profilePhotoPaths = messages
      .map((msg) => msg.sender?.profile_photo_url)
      .filter((p): p is string => !!p);
    const signedUrlMap = await batchGetProfilePhotoSignedUrls(
      this.client,
      profilePhotoPaths,
    );

    // Transform to MessageWithSender (messages are already in newest-first order)
    const messagesWithSender: MessageWithSender[] = messages.map((msg) => {
      const senderPhotoPath = msg.sender?.profile_photo_url;
      const senderPhotoUrl = senderPhotoPath
        ? (signedUrlMap.get(senderPhotoPath) ?? null)
        : null;

      return {
        ...msg,
        sender: msg.sender
          ? {
              ...msg.sender,
              profile_photo_url: senderPhotoUrl,
            }
          : msg.sender_id
            ? {
                id: msg.sender_id,
                first_name: null,
                last_name: null,
                profile_photo_url: null,
              }
            : null,
        reply_to: null,
      } as MessageWithSender;
    });

    return Result.ok({
      messages: messagesWithSender,
      nextCursor,
    });
  }

  /**
   * Send a new message to a conversation
   */
  async send(params: SendMessageParams): Promise<Result<Message>> {
    const {
      conversationId,
      content,
      contentType = 'text',
      attachments: _attachments,
      replyToId,
      metadata,
    } = params;

    // Validate conversation ID
    const uuidValidation = this.validateUUID(conversationId, 'conversationId');
    if (uuidValidation.isFailure) {
      return Result.fail(uuidValidation.getError());
    }

    // Validate content
    if (!content || content.trim().length === 0) {
      return Result.fail(
        new ServiceError('VALIDATION_ERROR', 'Message content is required'),
      );
    }

    // Validate reply_to if provided
    if (replyToId) {
      const replyValidation = this.validateUUID(replyToId, 'replyToId');
      if (replyValidation.isFailure) {
        return Result.fail(replyValidation.getError());
      }
    }

    // Get current user
    const {
      data: { user },
    } = await this.client.auth.getUser();
    if (!user) {
      return Result.fail(
        new ServiceError('UNAUTHORIZED', 'User not authenticated'),
      );
    }

    // BAL-580: in a thread addressed to an organization, everyone but the
    // person who opened it replies AS the org, through the one function that
    // writes an organization message and its internal author row. A direct
    // insert there is refused by the database (guard_message_sender).
    const { data: conversation, error: conversationError } = await this.client
      .from('conversations')
      .select('organization_id, created_by')
      .eq('id', conversationId)
      .maybeSingle();

    if (conversationError) {
      return Result.fail(this.handleDatabaseError(conversationError));
    }

    if (conversation?.organization_id && conversation.created_by !== user.id) {
      const { data: messageId, error: rpcError } = await this.client.rpc(
        'send_organization_message',
        {
          p_conversation_id: conversationId,
          p_content: content.trim(),
          p_content_type: contentType,
          p_reply_to_id: replyToId || undefined,
          p_metadata: metadata ?? undefined,
        },
      );

      if (rpcError || !messageId) {
        return Result.fail(
          rpcError
            ? this.handleDatabaseError(rpcError)
            : new ServiceError('CREATE_FAILED', 'Message was not created'),
        );
      }

      const { data: orgMessage, error: readError } = await this.client
        .from('messages')
        .select()
        .eq('id', messageId)
        .single();

      if (readError) {
        return Result.fail(this.handleDatabaseError(readError));
      }

      return Result.ok(orgMessage as Message);
    }

    const { data, error } = await this.client
      .from('messages')
      .insert({
        conversation_id: conversationId,
        sender_id: user.id,
        content: content.trim(),
        content_type: contentType,
        reply_to_id: replyToId || null,
        metadata: metadata ?? null,
      })
      .select()
      .single();

    if (error) {
      return Result.fail(this.handleDatabaseError(error));
    }

    return Result.ok(data as Message);
  }

  /**
   * Edit an existing message
   * Only the sender can edit their own messages
   */
  async edit(messageId: string, content: string): Promise<Result<void>> {
    const uuidValidation = this.validateUUID(messageId, 'messageId');
    if (uuidValidation.isFailure) {
      return Result.fail(uuidValidation.getError());
    }

    if (!content || content.trim().length === 0) {
      return Result.fail(
        new ServiceError('VALIDATION_ERROR', 'Message content is required'),
      );
    }

    const { error } = await this.client
      .from('messages')
      .update({
        content: content.trim(),
        is_edited: true,
        edited_at: new Date().toISOString(),
      })
      .eq('id', messageId);

    if (error) {
      return Result.fail(this.handleDatabaseError(error));
    }

    return Result.ok(undefined);
  }

  /**
   * Soft delete a message
   * Only the sender can delete their own messages
   */
  async delete(messageId: string): Promise<Result<void>> {
    const uuidValidation = this.validateUUID(messageId, 'messageId');
    if (uuidValidation.isFailure) {
      return Result.fail(uuidValidation.getError());
    }

    const { error } = await this.client
      .from('messages')
      .update({
        is_deleted: true,
        deleted_at: new Date().toISOString(),
        content: null, // Clear content on deletion
      })
      .eq('id', messageId);

    if (error) {
      return Result.fail(this.handleDatabaseError(error));
    }

    return Result.ok(undefined);
  }

  /**
   * Get a single message by ID
   */
  async getMessage(messageId: string): Promise<Result<MessageWithSender>> {
    const uuidValidation = this.validateUUID(messageId, 'messageId');
    if (uuidValidation.isFailure) {
      return Result.fail(uuidValidation.getError());
    }

    const { data, error } = await this.client
      .from('messages')
      .select(
        `
        *,
        sender:profiles!messages_sender_id_profiles_fkey(id, first_name, last_name, profile_photo_url),
        ${MESSAGE_ORG_SENDER_EMBEDS}
      `,
      )
      .eq('id', messageId)
      .single();

    if (error) {
      return Result.fail(this.handleDatabaseError(error));
    }

    // Generate signed URL for sender profile photo (single item, use direct call)
    const senderPhotoUrl = data.sender?.profile_photo_url
      ? await getProfilePhotoSignedUrl(
          this.client,
          data.sender.profile_photo_url,
        )
      : null;

    const messageWithSender: MessageWithSender = {
      ...data,
      sender: data.sender
        ? {
            ...data.sender,
            profile_photo_url: senderPhotoUrl,
          }
        : data.sender_id
          ? {
              id: data.sender_id,
              first_name: null,
              last_name: null,
              profile_photo_url: null,
            }
          : null,
      reply_to: null,
    } as MessageWithSender;

    return Result.ok(messageWithSender);
  }
}
